import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Joi from 'joi';
import qs from 'query-string';

import {
    useCognitoSignInMutation,
    useCreatePasswordMutation,
    useResendInvitationMutation,
    useSetPasswordMutation,
} from 'api/main';
import { holdUpSet } from 'reducers/user';
import { investorErrors, PASS_REGEX, userChallenges } from 'utils/constants';
import { sanitizeURL, validateSchema } from 'utils/helpers';
import { useSmallScreen } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { LoggingRedirect } from 'routes/LoggingRedirect';
import TunnelBox from 'containers/tunnel/common/TunnelBox';
import TunnelStep from 'containers/tunnel/common/TunnelStep';
import { BigLoader, SubmitButton, PasswordField, Loader } from 'components';
import { Typography, Box, Link } from '@mui/material';

const SETUP_PASSWORD_QUERY_PARAMS = ['code', 'email', 'subscriptionHash'];

const schema = {
    password: Joi.string().pattern(PASS_REGEX).required().messages({
        'string.empty': texts.EMPTY_NEW_PASSWORD,
        'string.pattern.base': texts.LE_MOT_DE_PASSE_DOIT_CONTENIR,
    }),
    confirmPassword: Joi.when('password', {
        is: '',
        then: Joi.any(),
        otherwise: Joi.ref('password'),
    }).messages({
        'any.only': texts.PASSWORDS_MUST_MATCH,
    }),
};

const SetupPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isSmall = useSmallScreen();
    const sanitizedURL = useMemo(
        () =>
            sanitizeURL({
                url: window.location.href,
                paramList: SETUP_PASSWORD_QUERY_PARAMS,
            }),
        []
    );
    const sanitizedSearch = useMemo(() => new URL(sanitizedURL).search, [sanitizedURL]);
    // Safe to decode since sanitizeURL ensures valid percent-encoding.
    const { code, subscriptionHash, email } = useMemo(
        () => qs.parse(sanitizedSearch, { decode: true }),
        [sanitizedSearch]
    );
    const [
        cognitoSignIn,
        {
            isLoading: cognitoLoader,
            isError: loginFail,
            data: cognitoResponse,
            reset: resetCognitoSignIn,
        },
    ] = useCognitoSignInMutation({ fixedCacheKey: 'cognito-sign-in' });
    const [setPassword, { isLoading: setPassLoading, isError: setPassFail, error: setPassError }] =
        useSetPasswordMutation();
    const [
        createPassword,
        {
            isLoading: createPassLoading,
            isSuccess: createPassSuccess,
            isError: createPassFail,
            error: createPassError,
        },
    ] = useCreatePasswordMutation();

    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [errors, setErrors] = useState({ password: '', confirmPassword: '' });

    const storedEmail = useMemo(
        () => cognitoResponse?.challengeParam?.userAttributes?.email,
        [cognitoResponse]
    );

    const [resendInvitation, { isSuccess: resendSuccess, isLoading: resendLoader }] =
        useResendInvitationMutation();

    useEffect(() => {
        if (sanitizedURL !== window.location.href) {
            window.history.pushState({}, null, sanitizedURL);
        }
    }, [sanitizedURL]);

    useEffect(() => {
        if ((email || storedEmail) && code && !cognitoResponse && !createPassSuccess) {
            cognitoSignIn({ email: email || storedEmail, password: code });
        }
    }, [cognitoSignIn, email, code, storedEmail, cognitoResponse, createPassSuccess]);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        return valid;
    }, [form]);

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(oldValue => ({ ...oldValue, [key]: value }));
        },
        []
    );

    const handleSubmit = useCallback(() => {
        if (formIsValid()) {
            if (subscriptionHash) {
                setPassword({ subscriptionHash, password: form.password });
            } else if (!createPassSuccess) {
                createPassword({ password: form.password });
            }
        }
    }, [form, formIsValid, createPassSuccess, subscriptionHash, setPassword, createPassword]);

    const handleUserAccess = useCallback(
        e => {
            e.preventDefault();

            dispatch(holdUpSet(false));
            navigate('/dashboard');
        },
        [dispatch, navigate]
    );

    const handleResendInvite = useCallback(
        e => {
            e.preventDefault();

            resendInvitation(email || storedEmail);
        },
        [email, storedEmail, resendInvitation]
    );

    const handleKeyDown = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    useEffect(() => {
        if (createPassSuccess) {
            resetCognitoSignIn();
        }
    }, [createPassSuccess, resetCognitoSignIn]);

    useEffect(() => {
        if (setPassFail) {
            const errMsg = setPassError.data?.detail?.split('password: ')?.[1];
            if (errMsg) {
                setErrors(oldErrors => ({ ...oldErrors, password: errMsg }));
            }
        }
    }, [setPassFail, setPassError]);

    useEffect(() => {
        if (createPassFail) {
            const errMsg = createPassError.data?.detail?.split('password: ')?.[1];
            if (errMsg) {
                setErrors(oldErrors => ({ ...oldErrors, password: errMsg }));
            }
        }
    }, [createPassFail, createPassError]);

    if (
        ((!storedEmail && !email) || !code) &&
        cognitoResponse?.nextStep?.signInStep !== userChallenges.NEW_PASSWORD_REQUIRED &&
        !setPassLoading &&
        !loginFail &&
        sanitizedURL === window.location.href &&
        !subscriptionHash
    ) {
        return <LoggingRedirect origin="SETUP_PASSWORD" to="/sign-in" />;
    }

    return cognitoLoader ? (
        <BigLoader />
    ) : (
        <Box sx={theme => ({ width: '100%' })}>
            <TunnelStep
                title={subscriptionHash ? texts.CREATE_PASSWORD : texts.FINALISER_VOTRE_INSCRIPTION}
            >
                {resendSuccess && (
                    <Typography gutterBottom variant="h6" align="center">
                        {texts.UN_NOUVEAU_MESSAGE_VOUS_SERA_ENVOYE}
                    </Typography>
                )}
                {loginFail && !resendSuccess ? (
                    <Loader loading={resendLoader}>
                        <Typography gutterBottom variant="h6" align="center">
                            {texts.LIEN_POUR_LA_CONFIRMATION_A_EXPIRE}{' '}
                            <Link
                                to="/dashboard"
                                href="/dashboard"
                                component={RouterLink}
                                onClick={handleResendInvite}
                                sx={theme => ({
                                    '&:hover, &:visited, &:active, &:focus': {
                                        color: theme.palette.primary.main,
                                    },
                                })}
                            >
                                {texts.CLICK_HERE}
                            </Link>{' '}
                            {texts.POUR_RENVOYER_UN_NOUVEAU_EMAIL}
                        </Typography>
                    </Loader>
                ) : (
                    !resendSuccess && (
                        <React.Fragment>
                            <TunnelBox>
                                <Box
                                    component="form"
                                    onSubmit={e => e.preventDefault()}
                                    sx={
                                        isSmall
                                            ? undefined
                                            : { display: 'flex', justifyContent: 'center' }
                                    }
                                >
                                    <Box
                                        sx={isSmall ? undefined : { flexBasis: '50%' }}
                                        onKeyDown={handleKeyDown}
                                    >
                                        <PasswordField
                                            sx={theme => ({
                                                maxWidth: theme.constants.tunnelInputWidth,
                                            })}
                                            label={texts.MOT_DE_PASSE}
                                            disabled={setPassLoading}
                                            value={form.password}
                                            onChange={handleChange('password')}
                                            error={errors.password}
                                        />
                                        <PasswordField
                                            sx={theme => ({
                                                maxWidth: theme.constants.tunnelInputWidth,
                                            })}
                                            label={texts.CONFIRM_PASSWORD}
                                            disabled={setPassLoading}
                                            value={form.confirmPassword}
                                            onChange={handleChange('confirmPassword')}
                                            error={errors.confirmPassword}
                                        />
                                        {createPassSuccess && (
                                            <Typography
                                                align="left"
                                                variant="caption"
                                                gutterBottom
                                                color="success.main"
                                            >
                                                {texts.INSCRIPTION_AVEC_SUCCES} {texts.CLICK}{' '}
                                                <Link
                                                    to="/dashboard"
                                                    onClick={handleUserAccess}
                                                    component={RouterLink}
                                                >
                                                    {texts.HERE}
                                                </Link>{' '}
                                                {texts.POUR_ACCEDER_A_VOTRE_COMPTE}
                                            </Typography>
                                        )}
                                        {setPassError?.data?.message ===
                                            investorErrors.ALREADY_ACTIVE && (
                                            <Typography align="left" variant="caption" gutterBottom>
                                                {texts.ACCOUNT_ALREADY_CONFIRMED} {texts.CLICK}{' '}
                                                <Link
                                                    to="/sign-in"
                                                    onClick={handleUserAccess}
                                                    component={RouterLink}
                                                >
                                                    {texts.HERE}
                                                </Link>{' '}
                                                {texts.POUR_VOUS_AUTHENTIFIER}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </TunnelBox>
                            <SubmitButton
                                text={texts.JE_M_INSCRIS}
                                onClick={handleSubmit}
                                disabled={createPassLoading}
                                loading={createPassLoading || setPassLoading}
                            />
                        </React.Fragment>
                    )
                )}
            </TunnelStep>
        </Box>
    );
};

export default SetupPassword;
